import { ChevronRight } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { ProductDetailView } from "@/components/product/product-detail-view";
import { ProductGrid } from "@/components/product/product-grid";
import { ProductTabs } from "@/components/product/product-tabs";
import RecentlyViewed from "@/components/product/RecentlyViewed";
import TrackView from "@/components/product/TrackView";
import { SectionHeading } from "@/components/ui/section-heading";
import { getProductDetail } from "@/lib/queries";
import { formatPrice } from "@/lib/utils";
import { NotFoundPage } from "@/pages/error-pages";

type ProductDetailPayload = Awaited<ReturnType<typeof getProductDetail>>;

/** `app/products/[slug]/page.tsx`. */
export function ProductDetailPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [data, setData] = useState<ProductDetailPayload | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let active = true;
    setMissing(false);
    void getProductDetail(slug).then((payload) => {
      if (!active) return;
      if (!payload) setMissing(true);
      else setData(payload);
    });
    return () => {
      active = false;
    };
  }, [slug]);

  // `notFound()` used to run before anything rendered; the 404 page takes its
  // place once we know the slug does not resolve.
  if (missing) return <NotFoundPage />;
  if (!data) return <ProductDetailSkeleton />;

  const { product, related, alsoBought, similar, reviews, bundle } = data;

  return (
    <div className="mx-auto max-w-[1500px] px-3 py-3 sm:px-4 sm:py-4">
      <nav className="mb-3 flex flex-wrap items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
        <Link href="/" className="hover:text-amazon-orange hover:underline">
          Home
        </Link>
        {product.categories && product.categories.length >= 2 ? (
          <>
            <ChevronRight className="h-3 w-3" />
            <Link
              href={`/search?category=${encodeURIComponent(product.categories[0])}`}
              className="hover:text-amazon-orange hover:underline"
            >
              {product.categories[0]}
            </Link>
            <ChevronRight className="h-3 w-3" />
            <Link
              href={`/search?category=${encodeURIComponent(product.categories[1])}`}
              className="hover:text-amazon-orange hover:underline font-medium text-slate-700 dark:text-slate-300"
            >
              {product.categories[1]}
            </Link>
          </>
        ) : (
          <>
            <ChevronRight className="h-3 w-3" />
            <Link
              href={`/search?category=${encodeURIComponent(product.category)}`}
              className="hover:text-amazon-orange hover:underline font-medium text-slate-700 dark:text-slate-300"
            >
              {product.category}
            </Link>
          </>
        )}
        <ChevronRight className="h-3 w-3" />
        <span className="line-clamp-1 text-slate-700 dark:text-slate-300">{product.title}</span>
      </nav>

      <TrackView product={product} />

      <ProductDetailView product={product} />

      <div className="mt-6 rounded border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-slate-900 sm:p-5">
        <ProductTabs product={product} reviews={reviews} />
      </div>

      {bundle.length > 1 && (
        <section className="mt-6 rounded border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900 sm:p-5">
          <SectionHeading title="Frequently bought together" />
          <div className="flex flex-wrap items-center gap-3">
            {bundle.map((item, i) => (
              <div key={item.id} className="flex items-center gap-3">
                {i > 0 && <span className="text-xl text-slate-300">+</span>}
                <Link href={`/products/${item.slug}`} className="w-28 sm:w-36 rounded border border-slate-200 p-2 text-center text-xs hover:border-amazon-orange dark:border-white/10">
                  <span className="line-clamp-2">{item.title}</span>
                  <p className="mt-1.5 font-bold text-slate-950 dark:text-white">{formatPrice(item.price)}</p>
                </Link>
              </div>
            ))}
          </div>
        </section>
      )}

      {similar.length > 0 && (
        <section className="mt-6 rounded border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900 sm:p-5">
          <SectionHeading title="Similar products" href={`/search?q=${encodeURIComponent(product.brand)}`} />
          <ProductGrid products={similar} />
        </section>
      )}

      {alsoBought.length > 0 && (
        <section className="mt-6 rounded border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900 sm:p-5">
          <SectionHeading title="Customers also bought" href="/search" />
          <ProductGrid products={alsoBought} />
        </section>
      )}

      {related.length > 0 && (
        <section className="mt-6 rounded border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900 sm:p-5">
          <SectionHeading title="Related products" href={`/search?category=${encodeURIComponent(product.category)}`} />
          <ProductGrid products={related} />
        </section>
      )}

      <RecentlyViewed />
    </div>
  );
}

function ProductDetailSkeleton() {
  return (
    <div className="mx-auto max-w-[1500px] px-3 py-3 sm:px-4 sm:py-4">
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="h-96 animate-pulse rounded-lg bg-slate-100 dark:bg-white/5" />
        <div className="space-y-3">
          <div className="h-5 w-3/4 animate-pulse rounded bg-slate-100 dark:bg-white/5" />
          <div className="h-4 w-1/2 animate-pulse rounded bg-slate-100 dark:bg-white/5" />
          <div className="h-10 w-40 animate-pulse rounded bg-slate-100 dark:bg-white/5" />
        </div>
      </div>
    </div>
  );
}